import { isBefore, parseISO, startOfToday } from "date-fns"
import PropTypes from "prop-types"
import React, { useContext } from "react"

import { DangerousDeprecatedOldReactAdapterComponent } from "src/components/__dangerous__"

import { getCurrentMarinaSlug } from "src/utils/url/parsing/marina"

import CancelContractModal from "./CancelContractModal"
import FeatureFlagContextProvider from "./FeatureFlagContext"
import Items from "./Items"
import LegacyItinerarySection from "./LegacyItinerarySection"
import LegacyNightsSection from "./LegacyNightsSection"
import Payments from "./Payments"
import ReservationDatesContextProvider from "./ReservationDatesContext"
import SharedBillingContextProvider, {
  SharedBillingContext,
} from "./SharedBillingContext"
import { hasDisputedInvoice } from "./helpers"

const Billing = ({
  ledgerId,
  paymentMethods,
  isMonthToMonth,
  isMonthlyInstallments,
  pricingUrl,
  monthlyRateOptions,
  contactBoat,
  ratePreservationEnabled,
  hasPreservedRates,
  editable,
  editInvoiceDueDates,
  cancellationPolicyPath = null,
  cancellationEntitlement = null,
  reservationDepositRemainderDue,
  contractSurchargeEligible,
  useNewTaxExperience = false,
}) => {
  const marinaSlug = getCurrentMarinaSlug()

  const {
    invoices: {
      data: invoiceData,
      isLoading: isLoadingInvoices,
      isError: isInvoicesError,
    },
    items: {
      collapsedData: items,
      isLoading: isLoadingItems,
      isError: isItemsError,
    },
    refreshItems,
    refreshPayments,
    reservationId,
    contractQuoteId,
  } = useContext(SharedBillingContext)

  const scheduledInvoices =
    invoiceData?.filter(
      (invoice) =>
        invoice.display_status === "Scheduled" &&
        !isBefore(parseISO(invoice.due_date), startOfToday())
    ) || []
  const hasScheduledInvoice = scheduledInvoices.length > 0

  return (
    <>
      <h3 className="text-lg font-semibold">Billing</h3>
      <Items
        items={items}
        ledgerId={ledgerId}
        contractQuoteId={contractQuoteId}
        reservationId={reservationId}
        paymentMethods={paymentMethods}
        refreshItems={refreshItems}
        refreshPayments={refreshPayments}
        isLoading={isLoadingItems}
        isError={isItemsError}
        isMonthToMonth={isMonthToMonth}
        isMonthlyInstallments={isMonthlyInstallments}
        hasScheduledInvoice={hasScheduledInvoice}
        pricingUrl={pricingUrl}
        monthlyRateOptions={monthlyRateOptions}
        contactBoat={contactBoat}
        ratePreservationEnabled={ratePreservationEnabled}
        hasPreservedRates={hasPreservedRates}
        editable={editable}
        reservationDepositRemainderDue={reservationDepositRemainderDue}
        useNewTaxExperience={useNewTaxExperience}
      />
      <Payments
        payments={invoiceData}
        refreshItems={refreshItems}
        refreshPayments={refreshPayments}
        reservationId={reservationId}
        marinaSlug={marinaSlug}
        paymentMethods={paymentMethods}
        isLoading={isLoadingInvoices}
        isError={isInvoicesError}
        editable={editable}
        editInvoiceDueDates={editInvoiceDueDates}
      />
      <CancelContractModal
        reservationId={reservationId}
        contractQuoteId={contractQuoteId}
        hasDisputes={hasDisputedInvoice(invoiceData)}
        deprecatedButtonTargetId="cancel-contract-modal-mbm-button"
        cancellationPolicyPath={cancellationPolicyPath}
        cancellationEntitlement={cancellationEntitlement}
        useNewTaxExperience={useNewTaxExperience}
      />
      <DangerousDeprecatedOldReactAdapterComponent
        targetId="itinerary-section-mbm"
        weAcknowledgeThisTechniqueIsDangerous
      >
        <LegacyItinerarySection contractQuoteId={contractQuoteId} />
      </DangerousDeprecatedOldReactAdapterComponent>
      <DangerousDeprecatedOldReactAdapterComponent
        targetId="nights-section-mbm"
        weAcknowledgeThisTechniqueIsDangerous
      >
        <LegacyNightsSection />
      </DangerousDeprecatedOldReactAdapterComponent>
    </>
  )
}

const BillingContainer = ({
  featureFlags,
  reservationCheckInDate,
  reservationCheckOutDate,
  reservationId,
  billingContainerId = null,
  contractQuoteId,
  contractSurchargeEligible = false,
  lateFee = null,
  ...props
}) => {
  return (
    <ReservationDatesContextProvider
      reservationCheckInDate={reservationCheckInDate}
      reservationCheckOutDate={reservationCheckOutDate}
    >
      <SharedBillingContextProvider
        reservationId={reservationId}
        billingContainerId={billingContainerId}
        contractQuoteId={contractQuoteId}
        editable={props.editable}
        contractSurchargeEligible={contractSurchargeEligible}
        lateFee={lateFee}
      >
        <FeatureFlagContextProvider featureFlags={featureFlags}>
          <Billing {...props} />
        </FeatureFlagContextProvider>
      </SharedBillingContextProvider>
    </ReservationDatesContextProvider>
  )
}

Billing.propTypes = {
  ledgerId: PropTypes.string.isRequired,
  paymentMethods: PropTypes.object.isRequired,
  isMonthToMonth: PropTypes.bool.isRequired,
  isMonthlyInstallments: PropTypes.bool.isRequired,
  pricingUrl: PropTypes.string.isRequired,
  monthlyRateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  contactBoat: PropTypes.shape({
    lengthOverall: PropTypes.number.isRequired,
    beam: PropTypes.number.isRequired,
    squareFeet: PropTypes.number.isRequired,
  }),
  ratePreservationEnabled: PropTypes.bool.isRequired,
  hasPreservedRates: PropTypes.bool.isRequired,
  editable: PropTypes.bool.isRequired,
  editInvoiceDueDates: PropTypes.bool.isRequired,
  cancellationPolicyPath: PropTypes.string,
  cancellationEntitlement: PropTypes.shape({
    outcome: PropTypes.string,
    label: PropTypes.string,
  }),
  reservationDepositRemainderDue: PropTypes.oneOf([
    "monthly",
    "check_in",
    "check_out",
  ]),
  useNewTaxExperience: PropTypes.bool,
}

BillingContainer.propTypes = {
  ...Billing.propTypes,
  featureFlags: PropTypes.shape({
    transientEditDates: PropTypes.bool,
  }),
  reservationCheckInDate: PropTypes.string.isRequired,
  reservationCheckOutDate: PropTypes.string,
  contractQuoteId: PropTypes.number,
  reservationId: PropTypes.number.isRequired,
  billingContainerId: PropTypes.number,
  lateFee: PropTypes.shape({
    amount: PropTypes.number,
    gracePeriodDays: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    percentAmount: PropTypes.number,
    productId: PropTypes.string.isRequired,
    reservationId: PropTypes.number.isRequired,
  }),
}

export default BillingContainer
